import { HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { from, switchMap } from 'rxjs';
import { isReallyOnline } from '@wheere-front/shared-util';

/** URL prefixes that must NEVER carry a Bearer header AND must NEVER block on
 * Keycloak being initialized. Static assets (i18n JSON, SVG icons, fonts) are
 * fetched by Angular *before* APP_INITIALIZER finishes (transloco's
 * load(active) runs in parallel with keycloak.init), so calling
 * `service.getToken()` here can throw `Cannot read properties of undefined
 * (reading 'token')` and silently break the i18n bootstrap — which is exactly
 * how every component started rendering raw transloco keys in prod. Mirrors
 * the Keycloak bearerExcludedUrls config in app.config.ts. */
const PUBLIC_URL_PREFIXES = ['/assets/', 'assets/'];

function isPublicAsset(url: string): boolean {
  return PUBLIC_URL_PREFIXES.some(prefix => url.startsWith(prefix) || url.includes(`/${prefix}`));
}

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  // Bypass static assets entirely — no online check, no Keycloak token call.
  if (isPublicAsset(req.url)) {
    return next(req);
  }

  const service = inject(KeycloakService);

  return from(isReallyOnline()).pipe(
    switchMap(online => {
      if (!online) {
        return next(req);
      }

      return from(service.getToken()).pipe(
        switchMap(token => {
          if (!token) {
            return next(req);
          }

          const newReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`),
          });

          return next(newReq);
        })
      );
    })
  );
}
